import { WebinarViewingUpdateRequest, WebinarViewingUpdateRequestEventTypeEnum } from "@/api/generated";
import { isResponselessNetworkError } from "@/utilities/networkError";

export const PROGRESS_MIN_INTERVAL_MS = 5000;

/**
 * プレイヤーの再生状態のスナップショット。
 * HTML5 video の paused / seeking と同じ意味論（YouTube プレイヤー側は seeking=false 固定で渡す）。
 */
export type PlaybackSnapshot = {
  paused: boolean;
  seeking: boolean;
};

export type PersistProgressOptions = {
  completed?: boolean;
  reason?: WebinarViewingUpdateRequestEventTypeEnum;
  force?: boolean;
};

export type ViewingTrackerDeps = {
  /** 視聴セッションを開始し viewing_history の id を返す（失敗時は throw してよい） */
  createViewing: () => Promise<string | null>;
  /** 視聴ログを更新する（失敗時は throw してよい） */
  updateViewing: (viewingId: string, payload: WebinarViewingUpdateRequest) => Promise<unknown>;
  /** テスト用に注入可能な現在時刻（ms） */
  now?: () => number;
  /** progress 送信のスロットル間隔（ms） */
  progressMinIntervalMs?: number;
};

/**
 * ウェビナー視聴ログの時間追跡・送信ロジック。
 * detail.tsx の <video> イベント実装から抽出したもので、HTML5 video / YouTube IFrame Player の
 * 両方から同じ挙動で利用できるよう、プレイヤー状態は PlaybackSnapshot として受け取る。
 * 挙動（スロットル・積算・イベント種別）は抽出前の実装と同等に保つこと。
 */
export class WebinarViewingTracker {
  /** 概要欄リンク遷移などで最終 manual 送信を抑止するためのフラグ（conversion 送信と併用） */
  suppressManualEvent = false;

  private readonly createViewing: ViewingTrackerDeps["createViewing"];
  private readonly updateViewing: ViewingTrackerDeps["updateViewing"];
  private readonly now: () => number;
  private readonly progressMinIntervalMs: number;

  private viewingId: string | null = null;
  private viewingCreationPromise: Promise<string | null> | null = null;
  private lastPositionSeconds = 0;
  private lastProgressSentAt = 0;
  private totalWatchedSeconds = 0;
  private lastPlaybackTimestamp: number | null = null;
  private pendingSeek = false;
  private started = false;

  constructor(deps: ViewingTrackerDeps) {
    this.createViewing = deps.createViewing;
    this.updateViewing = deps.updateViewing;
    this.now = deps.now ?? (() => Date.now());
    this.progressMinIntervalMs = deps.progressMinIntervalMs ?? PROGRESS_MIN_INTERVAL_MS;
  }

  // MEMO: tsconfig が target 未指定（既定 ES3）のため getter 構文（TS1056）は使わずメソッドにしている
  hasStarted(): boolean {
    return this.started;
  }

  hasViewingSession(): boolean {
    return this.viewingId !== null;
  }

  getLastPosition(): number {
    return this.lastPositionSeconds;
  }

  getTotalWatched(): number {
    return this.totalWatchedSeconds;
  }

  /** 再生開始（play イベント / PlayerState.PLAYING） */
  handlePlay(): void {
    this.started = true;
    this.lastPlaybackTimestamp = this.now();
    this.pendingSeek = false;
    void this.ensureViewingSession();
  }

  /** 再生中の時間更新（timeupdate イベント / ポーリング） */
  handleTimeUpdate(currentTime: number, snapshot: PlaybackSnapshot): void {
    if (!Number.isFinite(currentTime) || currentTime < 0) {
      return;
    }
    if (snapshot.seeking) {
      this.lastPlaybackTimestamp = this.now();
    } else if (!snapshot.paused) {
      this.accumulatePlaybackDuration(snapshot);
    }
    void this.persistProgress(currentTime);
  }

  /** 一時停止（pause イベント / PlayerState.PAUSED） */
  handlePause(currentTime: number, snapshot: PlaybackSnapshot): void {
    if (!Number.isFinite(currentTime) || currentTime < 0) {
      return;
    }
    this.accumulatePlaybackDuration(snapshot, { force: true });
    this.pendingSeek = false;
    void this.persistProgress(currentTime, {
      reason: WebinarViewingUpdateRequestEventTypeEnum.Pause,
      force: true,
    });
  }

  /** 再生終了（ended イベント / PlayerState.ENDED）。duration が有効ならそれを最終位置として使う */
  handleEnded(currentTime: number, duration: number | undefined, snapshot: PlaybackSnapshot): void {
    const position = typeof duration === "number" && Number.isFinite(duration) && duration > 0 ? duration : currentTime;
    if (!Number.isFinite(position) || position < 0) {
      return;
    }
    this.accumulatePlaybackDuration(snapshot, { force: true });
    void this.persistProgress(position, {
      reason: WebinarViewingUpdateRequestEventTypeEnum.Ended,
      completed: true,
      force: true,
    });
  }

  /** シーク完了（seeked イベント） */
  handleSeeked(currentTime: number, snapshot: PlaybackSnapshot): void {
    if (!Number.isFinite(currentTime) || currentTime < 0) {
      return;
    }
    this.pendingSeek = false;
    this.lastPlaybackTimestamp = snapshot.paused ? null : this.now();
    this.lastPositionSeconds = currentTime;
    void this.persistProgress(currentTime, { force: true });
  }

  /**
   * 画面非表示（hidden）・離脱（manual）などでの最終送信。
   * 視聴が開始していない・セッションが無い場合は何もしない。
   * manual は suppressManualEvent が立っている場合、積算だけ行い送信しない（既存挙動）。
   */
  flush(reason: WebinarViewingUpdateRequestEventTypeEnum, snapshot: PlaybackSnapshot | null): void {
    if (!this.started || !this.viewingId) {
      return;
    }
    if (snapshot) {
      this.accumulatePlaybackDuration(snapshot, { force: true });
    }
    if (reason === WebinarViewingUpdateRequestEventTypeEnum.Manual && this.suppressManualEvent) {
      return;
    }
    void this.persistProgress(this.lastPositionSeconds, { reason, force: true });
  }

  /**
   * 実視聴時間の積算。前回の再生タイムスタンプからの経過（wall clock）を加算する。
   * 抽出元 detail.tsx の accumulatePlaybackDuration と同一ロジック。
   */
  accumulatePlaybackDuration(snapshot: PlaybackSnapshot, options?: { force?: boolean }): void {
    const now = this.now();
    const last = this.lastPlaybackTimestamp;
    const isPlaying = !snapshot.paused && !snapshot.seeking;
    const shouldAccumulate = last !== null && (isPlaying || options?.force);
    if (shouldAccumulate) {
      const deltaSeconds = (now - (last as number)) / 1000;
      if (Number.isFinite(deltaSeconds) && deltaSeconds > 0 && deltaSeconds < 3600) {
        this.totalWatchedSeconds += deltaSeconds;
      }
    }
    if (isPlaying) {
      this.lastPlaybackTimestamp = now;
    } else if (options?.force) {
      this.lastPlaybackTimestamp = null;
    } else if (snapshot.seeking) {
      this.lastPlaybackTimestamp = now;
    } else {
      this.lastPlaybackTimestamp = null;
    }
  }

  /** 視聴セッションを開始する（多重 POST 防止付き） */
  async ensureViewingSession(): Promise<string | null> {
    if (this.viewingId) {
      return this.viewingId;
    }
    if (this.viewingCreationPromise) {
      return this.viewingCreationPromise;
    }
    const promise = this.createViewing()
      .then((viewingId) => {
        if (viewingId) {
          this.viewingId = viewingId;
          return viewingId;
        }
        return null;
      })
      .catch((createError) => {
        console.error("視聴ログの開始に失敗しました", createError);
        return null;
      })
      .finally(() => {
        this.viewingCreationPromise = null;
      });
    this.viewingCreationPromise = promise;
    return promise;
  }

  /** 視聴ログの更新送信（progress はスロットル、それ以外は force で即時送信） */
  async persistProgress(positionSeconds: number, options?: PersistProgressOptions): Promise<void> {
    if (!Number.isFinite(positionSeconds) || positionSeconds < 0) {
      return;
    }
    if (!this.started) {
      return;
    }
    const {
      completed = false,
      reason = WebinarViewingUpdateRequestEventTypeEnum.Progress,
      force = false,
    } = options ?? {};
    this.lastPositionSeconds = positionSeconds;

    const now = this.now();
    const timeDelta = now - this.lastProgressSentAt;
    const shouldSendForSeek = this.pendingSeek;
    if (!force && !completed) {
      if (shouldSendForSeek) {
        this.pendingSeek = false;
      } else if (this.lastProgressSentAt !== 0 && timeDelta < this.progressMinIntervalMs) {
        return;
      }
    } else {
      this.pendingSeek = false;
    }

    const viewingId = this.viewingId;
    if (!viewingId) {
      return;
    }
    const payload: WebinarViewingUpdateRequest = {
      position_seconds: positionSeconds,
      total_watched_seconds: Math.max(0, Number(this.totalWatchedSeconds.toFixed(3))),
      completed,
      event_type: reason,
    };
    try {
      await this.updateViewing(viewingId, payload);
      this.lastProgressSentAt = now;
    } catch (updateError) {
      // 端末のオフライン・電波断などレスポンスを伴わない通信失敗は、progress が 5 秒ごとに累積値で
      // 送り直されるため想定内として info に留める。
      // CaptureConsole（src/index.tsx）は error / warn のみ拾うため、info であれば Sentry には送られない。
      // 完了（completed: true）の送信失敗は視聴完了が記録されないため error のまま残す。
      if (!completed && isResponselessNetworkError(updateError)) {
        console.info("視聴ログの更新をスキップしました（通信エラー）", updateError);
        return;
      }
      console.error("視聴ログの更新に失敗しました", updateError);
    }
  }
}
